/*
 * Copyright (c) 2026 GS Elektromedizinische Geraete G. Stemple GmbH, Germany - all rights reserved.
 * <p>
 * DO NOT ALTER OR REMOVE COPYRIGHT NOTICES OR THIS FILE HEADER.
 */

import { Autocomplete, createFilterOptions, FilterOptionsState, TextField } from '@mui/material';
import { SyntheticEvent } from 'react';
import { createCustomOption, isCustomOption } from './CreateableAutocomplete.functions';

export interface OptionValueType<T> {
  /**
   * When not undefined this indicates that the value was created with custom input.
   */
  customInputValue?: string;
  /**
   * The label displayed in the options list.
   */
  label: string;
  /**
   * The value of the option
   */
  value: T;
}

export type CreatableAutocompleteProps<T> = {
  readonly value?: OptionValueType<T>;
  readonly options: OptionValueType<T>[];
  /**
   * Creates the value for the newly input value.
   * @param inputValue The value that is inputted manually.
   */
  readonly newValueCreatorFunction: (inputValue: string) => T;
  readonly onChange?: (value: OptionValueType<T>) => void;
  readonly label?: string;
};

/**
 * Autocomplete that provides functionality to create new values.
 */
export default function CreatableAutocomplete<T>(props: CreatableAutocompleteProps<T>) {
  const { value, options, newValueCreatorFunction, onChange, label } = props;
  const filter = createFilterOptions<OptionValueType<T>>();

  function onSelectionChange(_: SyntheticEvent<Element, Event>, newValue: string | OptionValueType<T> | null) {
    if (newValue === null) {
      return;
    }
    if (typeof newValue === 'string') {
      // in case user did only type in the value, but did not click on it (returning only a string and now object),
      // we need to check for custom options (input might be exactly matching an option or be a custom input) (see CWCONF-31155)
      if (isCustomOption<T>(newValue, options)) {
        onChange?.(createCustomOption<T>(newValue, newValueCreatorFunction));
      } else {
        onChange?.({
          label: newValue,
          value: newValueCreatorFunction(newValue),
        });
      }
    } else {
      onChange?.(newValue);
    }
  }

  function filterOptions(options: OptionValueType<T>[], params: FilterOptionsState<OptionValueType<T>>) {
    const filtered = filter(options, params);

    const { inputValue } = params;
    // Suggest the creation of a new value
    const isCustom = isCustomOption<T>(inputValue, options);
    if (inputValue !== '' && isCustom) {
      filtered.push(createCustomOption<T>(inputValue, newValueCreatorFunction));
    }

    return filtered;
  }

  const formatOption = (label: string, customInputValue?: string) => {
    if (customInputValue == null) {
      return <>{label}</>;
    }
    return (
      <span>
        {'Unbekannten Wirkstoff wählen: '}
        <span
          style={{
            background: '#fb7324',
            borderRadius: '15px',
            padding: '5px 15px',
            marginLeft: '10px',
            color: '#ffffff',
          }}>
          {label}
        </span>
      </span>
    );
  };

  return (
    <Autocomplete
      freeSolo
      selectOnFocus
      clearOnBlur
      handleHomeEndKeys
      value={value}
      onChange={onSelectionChange}
      options={options}
      filterOptions={filterOptions}
      getOptionLabel={(option): string => {
        if (typeof option === 'string') {
          return option;
        }
        if (option.customInputValue !== undefined) {
          return option.customInputValue;
        }
        return option.label;
      }}
      renderOption={({ key, ...otherProps }, option) => (
        <li key={key} {...otherProps}>
          {typeof option === 'string' ? option : formatOption(option.label, option.customInputValue)}
        </li>
      )}
      renderInput={(params) => <TextField {...params} label={label} />}
    />
  );
}
